/**
 * useWizardState - Custom hook for wizard state management
 *
 * Handles:
 * - Initial state from URL params or dashboard navigation state
 * - Step navigation (next, back, goToStep)
 * - Data updates and merging
 * - Highest completed step tracking
 *
 * Mongo is the SINGLE source of truth (see useAutoSave).  localStorage
 * backup/restore was removed 2026-04-17: it raced with the DB on page
 * reload and overwrote server-side cleanups with stale tab state.  The
 * "Previous session restored" flow and the wizard_backup key are gone.
 * On page load, data is loaded from /api/v1/claims/:id.  Period.
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import api from '../../../../utils/api';

interface WizardData {
  client_name: string;
  claimant_name: string;
  business_address: string;
  zone: string;
  business_type: string;
  financial_data: Record<string, any>;
  calculation_mode?: string;
  [key: string]: any;
}

interface NavigationState {
  freshDraftData?: any;
  freshDraftStep?: number;
  draftId?: string;
  fromDashboard?: boolean;
}

const EMPTY_DATA: WizardData = {
  client_name: '',
  claimant_name: '',
  business_address: '',
  zone: '',
  business_type: '',
  financial_data: {}
};

export function useWizardState(claimId?: string | null) {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // URL params (claimId now comes from useClaimId hook, not extracted here)
  const resumeReportId = searchParams.get('resume');
  const urlStep = searchParams.get('step');
  const urlSection = searchParams.get('section');

  // Navigation state from dashboard
  const navigationState = location.state as NavigationState | null;

  // Draft loading state — loading when we have a claimId to load data for
  const [loadingUrlDraft, setLoadingUrlDraft] = useState(!!claimId);
  const [urlDraftData, setUrlDraftData] = useState<any>(null);

  // Load claim/draft data when claimId is available
  useEffect(() => {
    if (claimId && !navigationState?.fromDashboard) {
      const loadClaimData = async () => {
        try {
          // Try Claims API first (SSoT)
          let claim = await api.get(`/api/v1/claims/${claimId}`, { showErrorToast: false }).catch(() => null);

          // Fallback to legacy draft endpoint
          if (!claim) {
            claim = await api.get(`/api/v1/reports/drafts/${claimId}`, { showErrorToast: false }).catch(() => null);
          }

          // Fallback to reports endpoint
          if (!claim) {
            claim = await api.get(`/api/v1/reports/${claimId}`, { showErrorToast: false }).catch(() => null);
          }

          if (claim) {
            console.log('✅ Loaded claim data:', claimId);

            // Extract data and ensure IDs are preserved
            const claimData = claim.data || claim;
            claimData.draft_id = claimId;
            claimData.id = claimData.id || claimId;

            setUrlDraftData(claimData);
          } else {
            // New claim with no data yet — not an error
            console.log('No existing data for claim:', claimId);
          }
        } catch (err) {
          console.error('Error loading claim data:', err);
        } finally {
          setLoadingUrlDraft(false);
        }
      };
      loadClaimData();
    } else {
      setLoadingUrlDraft(false);
    }
  }, [claimId, navigationState]);

  // Get initial state
  const getInitialState = useCallback(() => {
    // PRIORITY 0: Draft loaded from URL param
    if (urlDraftData) {
      const targetStep = urlStep ? parseInt(urlStep, 10) : 3;
      // If we have data, allow access to ALL steps (7 total)
      // This enables free navigation for loaded drafts
      const completedStep = 7; // Allow navigation to any step
      return {
        step: targetStep,
        highestCompletedStep: completedStep,
        data: urlDraftData,
        restored: false
      };
    }

    // PRIORITY 1: Fresh data from dashboard navigation
    if (navigationState?.fromDashboard && navigationState?.freshDraftData) {
      const targetStep = navigationState.freshDraftStep || 3;
      // Allow access to ALL steps for dashboard navigation
      const completedStep = 7; // Allow navigation to any step
      return {
        step: targetStep,
        highestCompletedStep: completedStep,
        data: navigationState.freshDraftData,
        restored: false
      };
    }

    // PRIORITY 2: Don't restore if resuming specific report
    if (resumeReportId) {
      return { step: 1, highestCompletedStep: 1, data: { ...EMPTY_DATA }, restored: false };
    }

    // No localStorage restore.  If we reach this point, URL/nav state didn't
    // give us data — start empty and let the claim load (useEffect above) or
    // the user's Step 1 entry populate the form.  Any stale wizard_backup key
    // that exists in localStorage from prior versions is proactively cleared.
    try { localStorage.removeItem('wizard_backup'); } catch { /* ignore */ }

    return { step: 1, highestCompletedStep: 1, data: { ...EMPTY_DATA }, restored: false };
  }, [urlDraftData, urlStep, navigationState, resumeReportId]);

  const initialState = getInitialState();
  const [step, setStep] = useState(initialState.step);
  const [data, setData] = useState<WizardData>(initialState.data);
  const [showRestoredBanner, setShowRestoredBanner] = useState(initialState.restored);
  const [highestCompletedStep, setHighestCompletedStep] = useState(initialState.highestCompletedStep);

  // Update state when URL draft data loads.
  //
  // Bug 7 (post-PR #204, fixed 2026-04-29): when "+ New Claim" creates a
  // fresh draft, the GET on the freshly-created claim returns a non-null
  // doc with empty `data`.  Previously this branch unconditionally jumped
  // to step 3 with `highestCompletedStep=7`, lighting phantom checkmarks
  // on Client Info + Event Details for a draft the user hasn't even
  // opened.  Distinguish "resuming an in-progress draft" from "fresh
  // empty draft we just minted" by checking whether the draft carries any
  // user-entered field — same set the `hasUnsavedChanges` derivation
  // below uses, kept in sync intentionally.
  useEffect(() => {
    if (!urlDraftData) return;
    setData(urlDraftData);

    const hasMeaningfulData = !!(
      urlDraftData.client_name ||
      urlDraftData.claimant_name ||
      urlDraftData.business_address ||
      urlDraftData.zone ||
      urlDraftData.business_type ||
      (urlDraftData.financial_data &&
        Object.keys(urlDraftData.financial_data).length > 0)
    );

    if (hasMeaningfulData) {
      // Resuming a draft the user has actually started — jump to step 3
      // (the heavy financial-data step is where most resumes land) and
      // unlock free navigation across all 7 steps.
      const targetStep = urlStep ? parseInt(urlStep, 10) : 3;
      setStep(targetStep);
      setHighestCompletedStep(7);
    } else {
      // Fresh empty draft — start at step 1 with no completed steps.
      // Honor an explicit `?step=` if the user navigated from a deep link.
      setStep(urlStep ? parseInt(urlStep, 10) : 1);
      setHighestCompletedStep(1);
    }
  }, [urlDraftData, urlStep]);

  // Auto-backup to localStorage REMOVED 2026-04-17.  Mongo (via useAutoSave)
  // is the single source of truth; the localStorage mirror was overwriting
  // server-side cleanups on page reload and creating phantom years.

  // Derive whether the user has unsaved changes worth protecting
  const hasUnsavedChanges = !!(data && step > 1 && (
    data.client_name ||
    data.claimant_name ||
    data.business_address ||
    data.zone ||
    data.business_type ||
    (data.financial_data && Object.keys(data.financial_data).length > 0)
  ));

  // Warn user on beforeunload (tab close / browser navigation) when there are unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // Navigation functions
  const updateData = useCallback((newData: Partial<WizardData>) => {
    setData(prev => ({ ...prev, ...newData }));
  }, []);

  const next = useCallback((newData: Partial<WizardData>) => {
    let processedData = { ...newData };

    // Check for yearly data format preservation
    const currentHasYearlyData = data.financial_data &&
      Object.keys(data.financial_data).some(key => key.startsWith('data_'));
    const newHasYearlyData = newData.financial_data &&
      Object.keys(newData.financial_data).some(key => key.startsWith('data_'));

    if (currentHasYearlyData && !newHasYearlyData && newData.financial_data && step !== 3) {
      processedData = { ...newData };
      delete processedData.financial_data;
    }

    const mergedData = { ...data, ...processedData };
    setData(mergedData);

    const targetStep = (mergedData as any).skipToStep || step + 1;
    setStep(targetStep);
    setHighestCompletedStep(prev => Math.max(prev, targetStep));
    // Persistence happens via useAutoSave → Mongo.  No localStorage mirror.
  }, [data, step]);

  const back = useCallback(() => {
    setStep(prev => prev - 1);
  }, []);

  const goToStep = useCallback((targetStep: number) => {
    setStep(targetStep);
  }, []);

  const clearAndStartFresh = useCallback(() => {
    localStorage.removeItem('wizard_backup');
    setStep(1);
    setData({ ...EMPTY_DATA });
    setShowRestoredBanner(false);
    setHighestCompletedStep(1);
  }, []);

  // F2-fix1 (2026-04-29): expose backend-computed completion_percentage so
  // wizard step sidebars can render the SSoT-correct value instead of
  // hardcoded mocks (75/85) or per-step local heuristics. The number is
  // populated by app.services.queue_assignment_service when the queue
  // assignment job runs over a draft; it lands on the claim doc and rides
  // through `urlDraftData.completion_percentage`. Undefined when the
  // backend hasn't computed yet — step components fall back to their
  // local calc / hardcoded value via `?? <existing>`.
  const backendCompletionPercent =
    typeof urlDraftData?.completion_percentage === 'number'
      ? urlDraftData.completion_percentage
      : undefined;

  return {
    // State
    step,
    data,
    showRestoredBanner,
    highestCompletedStep,
    loadingUrlDraft,
    backendCompletionPercent,

    // URL params
    resumeReportId,
    urlSection,

    // Actions
    setStep,
    setData,
    updateData,
    next,
    back,
    goToStep,
    clearAndStartFresh,
    setShowRestoredBanner,
    setHighestCompletedStep,
  };
}
